import isEqual from 'lodash/isEqual';
import { RefObject, createContext, createRef } from 'react';
import { createStore } from 'zustand';

import {
  JSONEditorInternalProps,
  JSONEditorState,
  JSONEditorStore,
} from './types';
import {
  createDuplicateKeyErrorMessage,
  findLastValueInput,
  findValueInputByIndex,
  focusElementAndSetCursorToEnd,
  isCursorAtBeginning,
  isDuplicateKey,
  isKeyValid,
  scheduleFocus,
  validateJSON,
} from './utils';

export const createJSONEditorStore = (
  initProps: Partial<JSONEditorInternalProps>
) => {
  const DEFAULT_PROPS: JSONEditorInternalProps = {
    value: { '': '' },
    setValue: () => {},
    readOnly: false,
    keyPlaceholder: 'Write key here...',
    valuePlaceholder: 'Write value here...',
    pendingKeyRef: null,
    pendingValueInputRef: null,
  };

  const value =
    initProps.value && Object.keys(initProps.value).length > 0
      ? initProps.value
      : DEFAULT_PROPS.value;

  const keyOrder = Object.keys(value);
  const keyInputRefs = keyOrder.reduce((acc, key) => {
    acc[key] = createRef<HTMLInputElement | HTMLTextAreaElement>();
    return acc;
  }, {} as Record<string, RefObject<HTMLInputElement | HTMLTextAreaElement>>);
  const valueInputRefs = keyOrder.reduce((acc, key) => {
    acc[key] = createRef<HTMLInputElement | HTMLTextAreaElement>();
    return acc;
  }, {} as Record<string, RefObject<HTMLInputElement | HTMLTextAreaElement>>);

  return createStore<JSONEditorState>()((set, get) => ({
    ...DEFAULT_PROPS,
    ...initProps,

    error: validateJSON(value),
    duplicateKeyError: null,
    localValues: value,
    keyOrder: Object.keys(value),
    pendingKey: '',
    showPendingInput: false,
    keyInputRefs: keyInputRefs,
    valueInputRefs: valueInputRefs,

    // State setters
    setError: (error: string | null) => set({ error }),
    setDuplicateKeyError: (error: string | null) =>
      set({ duplicateKeyError: error }),
    setPendingKey: (key: string) => set({ pendingKey: key }),
    setShowPendingInput: (show: boolean) => {
      const { focusLastValue } = get();
      set({ showPendingInput: show });
      // Use requestAnimationFrame to wait for next paint
      if (show) {
        requestAnimationFrame(() => {
          // Ensure DOM is updated
          requestAnimationFrame(() => {
            const { pendingKeyRef } = get();
            if (show && pendingKeyRef?.current) {
              scheduleFocus(pendingKeyRef.current);
            }
          });
        });
      } else {
        focusLastValue();
      }
    },
    setKeyInputRef: (
      key: string,
      ref: RefObject<HTMLInputElement | HTMLTextAreaElement>
    ) =>
      set((state) => ({
        keyInputRefs: { ...state.keyInputRefs, [key]: ref },
      })),
    setPendingKeyRef: (
      ref: RefObject<HTMLInputElement | HTMLTextAreaElement>
    ) => set({ pendingKeyRef: ref }),
    setPendingValueInputRef: (
      ref: RefObject<HTMLInputElement | HTMLTextAreaElement>
    ) => set({ pendingValueInputRef: ref }),

    updateValues: (() => {
      let timeoutId: NodeJS.Timeout;
      return (newValues: Record<string, string>) => {
        const { setValue, localValues } = get();
        if (isEqual(localValues, newValues)) {
          return;
        }
        set({ localValues: newValues });
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setValue(newValues);
          get().validateJSON(newValues);
        }, 300);
      };
    })(),

    validateJSON: (json: Record<string, string>): boolean => {
      const { setError } = get();
      const error = validateJSON(json);
      setError(error);
      return !!error;
    },

    updateKey: (oldKey: string, newKey: string) => {
      const {
        localValues,
        keyOrder,
        updateValues,
        setDuplicateKeyError,
        keyInputRefs,
        valueInputRefs,
      } = get();

      if (oldKey === newKey) return;

      if (isDuplicateKey(newKey, keyOrder, oldKey)) {
        setDuplicateKeyError(createDuplicateKeyErrorMessage(newKey));
        return;
      }

      setDuplicateKeyError(null);

      // Create new values object with updated key
      const newValues: Record<string, string> = {};
      const newOrder = keyOrder.map((k) => (k === oldKey ? newKey : k));

      newOrder.forEach((k) => {
        newValues[k] =
          k === newKey ? localValues[oldKey] : localValues[k] || '';
      });

      // Update refs for the new key
      const updatedKeyInputRefs = { ...keyInputRefs };
      const updatedValueInputRefs = { ...valueInputRefs };

      if (oldKey in updatedKeyInputRefs) {
        updatedKeyInputRefs[newKey] = updatedKeyInputRefs[oldKey];
        delete updatedKeyInputRefs[oldKey];
      }

      if (oldKey in updatedValueInputRefs) {
        updatedValueInputRefs[newKey] = updatedValueInputRefs[oldKey];
        delete updatedValueInputRefs[oldKey];
      }

      updateValues(newValues);
      set({
        keyOrder: newOrder,
        keyInputRefs: updatedKeyInputRefs,
        valueInputRefs: updatedValueInputRefs,
      });
    },

    updateValue: (key: string, value: string) => {
      const { localValues, updateValues } = get();
      updateValues({ ...localValues, [key]: value });
    },

    handleCancelNewInput: () => {
      const { setShowPendingInput, setPendingKey } = get();
      setShowPendingInput(false);
      setPendingKey('');
    },

    clearJSON: () => {
      const { updateValues, setError } = get();
      setError(null);
      updateValues({ '': '' });
      set({
        localValues: { '': '' },
        keyOrder: [''],
      });
    },

    handleAddKeyValue: (key: string, value: string, createNew = false) => {
      if (isKeyValid(key)) {
        const {
          localValues,
          keyOrder,
          updateValues,
          setDuplicateKeyError,
          setShowPendingInput,
          setPendingKey,
          keyInputRefs,
          valueInputRefs,
          focusLastValue,
        } = get();

        if (isDuplicateKey(key, keyOrder)) {
          setDuplicateKeyError(createDuplicateKeyErrorMessage(key));
          return false;
        }

        setDuplicateKeyError(null);

        // Update values and key order
        const newValues = { ...localValues, [key]: value };
        updateValues(newValues);

        // Create refs for the new key
        const updatedKeyInputRefs = { ...keyInputRefs };
        const updatedValueInputRefs = { ...valueInputRefs };
        updatedKeyInputRefs[key] = createRef<
          HTMLInputElement | HTMLTextAreaElement
        >();
        updatedValueInputRefs[key] = createRef<
          HTMLInputElement | HTMLTextAreaElement
        >();

        set({
          keyOrder: [...keyOrder, key],
          keyInputRefs: updatedKeyInputRefs,
          valueInputRefs: updatedValueInputRefs,
        });

        // Set up for new input
        setPendingKey('');

        // Only show the pending input if we're explicitly creating a new one
        // If we're adding via blur, we don't want to immediately show another pending input
        if (createNew) {
          setShowPendingInput(true);
        } else {
          setShowPendingInput(false);
          // Focus on the value of the newly added pair when added via blur
          requestAnimationFrame(() => {
            focusLastValue();
          });
        }

        return true;
      } else {
        const { pendingKeyRef } = get();
        // If key is empty but user tried to add, focus the key input
        if (pendingKeyRef?.current) {
          scheduleFocus(pendingKeyRef.current);
        }
        return false;
      }
    },

    deleteKey: (key: string, index: number, isLast: boolean) => {
      // Don't delete the only row if it's the first and last
      if (index === 0 && isLast) return;

      // Delete the key
      const {
        localValues,
        keyOrder,
        updateValues,
        keyInputRefs,
        valueInputRefs,
      } = get();

      // Create a new object without the deleted key
      const { [key]: _, ...newValues } = localValues;

      // Update the key order
      const newKeyOrder = keyOrder.filter((k) => k !== key);

      // Remove refs for the deleted key
      const updatedKeyInputRefs = { ...keyInputRefs };
      const updatedValueInputRefs = { ...valueInputRefs };
      delete updatedKeyInputRefs[key];
      delete updatedValueInputRefs[key];

      // Update the values and key order
      updateValues(newValues);
      set({
        keyOrder: newKeyOrder,
        keyInputRefs: updatedKeyInputRefs,
        valueInputRefs: updatedValueInputRefs,
      });
    },

    focusPreviousValue: (index: number) => {
      const { valueInputRefs } = get();
      if (index > 0) {
        // Find the previous value input
        const prevValueInput = findValueInputByIndex(index - 1, valueInputRefs);
        if (prevValueInput) {
          focusElementAndSetCursorToEnd(prevValueInput);
        }
      } else {
        // focus in index 0
        const firstValueInput = findValueInputByIndex(0, valueInputRefs);
        if (firstValueInput) {
          focusElementAndSetCursorToEnd(firstValueInput);
        }
      }
    },

    focusLastValue: () => {
      const { valueInputRefs } = get();
      const lastValueInput = findLastValueInput(valueInputRefs);
      if (lastValueInput) {
        focusElementAndSetCursorToEnd(lastValueInput);
      }
    },

    checkDuplicateKey: (key: string, currentKey?: string) => {
      const { keyOrder, setDuplicateKeyError } = get();

      if (isDuplicateKey(key, keyOrder, currentKey)) {
        setDuplicateKeyError(createDuplicateKeyErrorMessage(key));
        return true;
      } else {
        setDuplicateKeyError(null);
        return false;
      }
    },

    handleKeypressToNew: (
      isLast: boolean,
      nextKeyInputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      if (isLast) {
        // If this is the last row, trigger the callback to add a new input
        const { setShowPendingInput } = get();
        setShowPendingInput(true);
      } else if (nextKeyInputRef?.current) {
        // Otherwise focus the next key input
        scheduleFocus(nextKeyInputRef.current);
      }
    },

    // New event handler functions
    handleKeyInputKeyDown: (
      e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
      params: {
        isNewInput: boolean;
        localKeyName: string;
        value: string;
        keyName: string;
        index: number;
        isLast: boolean;
        valueInputRef: RefObject<HTMLInputElement | HTMLTextAreaElement>;
      }
    ) => {
      const {
        isNewInput,
        localKeyName,
        value,
        keyName,
        index,
        isLast,
        valueInputRef,
      } = params;
      const {
        readOnly,
        checkDuplicateKey,
        handleCancelNewInput,
        focusLastValue,
        deleteKey,
        focusPreviousValue,
      } = get();

      if ((e.key === 'Tab' || e.key === 'Enter') && !e.shiftKey) {
        e.preventDefault();

        // For new input mode, check for duplicate keys
        if (isNewInput) {
          if (checkDuplicateKey(localKeyName)) {
            return;
          }
        }

        // Move to value input
        scheduleFocus(valueInputRef.current);
      } else if (e.key === 'Backspace' && !readOnly) {
        // Check if cursor is at the beginning of the input
        const target = e.target as HTMLInputElement | HTMLTextAreaElement;
        if (isCursorAtBeginning(target)) {
          e.preventDefault();

          if (isNewInput) {
            // For new input, if key is empty, cancel the new input
            if (localKeyName === '') {
              handleCancelNewInput();
              focusLastValue();
            }
          } else {
            // For existing input, handle deletion or navigation
            if (value === '') {
              deleteKey(keyName, index, isLast);
              focusPreviousValue(index);
              return;
            }
          }
        }
      }
    },

    handleValueInputKeyDown: (
      e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
      params: {
        isNewInput: boolean;
        localKeyName: string;
        pendingValue: string;
        keyName: string;
        isLast: boolean;
        nextKeyInputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement>;
        setLocalKeyName: (key: string) => void;
        setPendingValue: (value: string) => void;
        setPendingKey?: (key: string) => void;
        keyInputRef: RefObject<HTMLInputElement | HTMLTextAreaElement>;
      }
    ) => {
      const { isNewInput, localKeyName, isLast, nextKeyInputRef, keyInputRef } =
        params;

      const { readOnly, handleKeypressToNew, handleCancelNewInput } = get();
      if ((e.key === 'Tab' || e.key === 'Enter') && !e.shiftKey) {
        e.preventDefault();

        if (localKeyName === '') {
          scheduleFocus(keyInputRef.current);
          return;
        }

        if (isNewInput) {
          // For new input, we no longer handle adding the key-value pair on Enter,
          // as this will now be handled by the onBlur of the key input.
          // Just focus back on the key input if needed
          scheduleFocus(keyInputRef.current);
        } else {
          // For existing input, handle navigation to next input
          handleKeypressToNew(isLast, nextKeyInputRef);
        }
      } else if (e.key === 'Escape' && isNewInput) {
        e.preventDefault();
        handleCancelNewInput();
      } else if (e.key === 'Backspace' && !readOnly) {
        // Check if cursor is at the beginning of the input
        const target = e.target as HTMLInputElement | HTMLTextAreaElement;
        const currentValue = isNewInput ? params.pendingValue : '';
        if (isCursorAtBeginning(target) && currentValue === '') {
          e.preventDefault();
          // Focus back on the key input
          if (keyInputRef.current) {
            scheduleFocus(keyInputRef.current);
          }
        }
      }
    },
  }));
};

export const PrettyJSONEditorContext = createContext<JSONEditorStore>(
  null as unknown as JSONEditorStore
);
